import React from 'react';

import { BRAND_DEPLOYMENT_SIM } from '../../../../theme/tokens';
import type { DeploymentStep, DeploymentStepState } from '../../../../mock-data/types';
import { CONFIRM_ID } from './types';
import type { TimelineEntry } from './types';

/** Steps that already ran (history) replay fast; the rest dwell so the glow reads. */
const HISTORY_STATES = new Set<DeploymentStepState>(['completed', 'failed', 'skipped']);

/**
 * Re-sequence the deployment steps into ONE canonical chronological deploy order
 * and mint an immutable replay timeline. Canonical order = history (completed by
 * timestamp, then failed, then skipped) → active → pending, so the live swap to
 * `status_details.steps` can never reorder the cards regardless of array order.
 *
 * Ids are a MONOTONIC counter (c0, c1, …), NOT `${i}-${label}` — the mock has
 * duplicate labels and a colliding key corrupts the enter/exit diff + ref map.
 * A synthetic confirmation entry is appended as the terminal card.
 *
 * Pure + exported so it is unit-tested without a DOM clock.
 */
export const buildTimeline = (steps: readonly DeploymentStep[]): TimelineEntry[] => {
  const byState = (state: DeploymentStepState): DeploymentStep[] => steps.filter((s) => s.state === state);
  const completed = byState('completed')
    .slice()
    .sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''));
  const ordered = [
    ...completed,
    ...byState('failed'),
    ...byState('skipped'),
    ...byState('active'),
    ...byState('pending'),
  ];

  const stepTotal = ordered.length;
  let counter = 0;
  const entries: TimelineEntry[] = ordered.map((step, index) => ({
    id: `c${counter++}`,
    label: step.label,
    settledState: HISTORY_STATES.has(step.state) ? (step.state as 'completed' | 'failed' | 'skipped') : 'completed',
    durationMs: HISTORY_STATES.has(step.state) ? BRAND_DEPLOYMENT_SIM.HISTORY_MS : BRAND_DEPLOYMENT_SIM.STEP_MS,
    stepNumber: index + 1,
    stepTotal,
    at: step.at,
  }));

  entries.push({ id: CONFIRM_ID, label: '', settledState: 'confirm', durationMs: 0, stepNumber: stepTotal, stepTotal });
  return entries;
};

export type DeploymentTimeline = {
  timeline: TimelineEntry[];
  /** Live cursor = the leading edge (1..length). Drives each card's status via statusFor. */
  cursor: number;
  /** True once the deploy has finished (the brand is no longer in progress). */
  done: boolean;
};

/**
 * Derive the strip's replay timeline + live cursor from the brand's REAL deploy steps.
 * There is NO timer/simulation: the cursor is the real leading edge — settled history
 * (completed/failed/skipped) fanned behind, the active step in progress at the front,
 * pending steps ahead. When the deploy finishes (`deploying === false`) the cursor jumps
 * to the terminal so the confirmation card appears as the final strip card. Advancement
 * comes purely from re-fetched data — the section polls the brand list while deploying —
 * so the ScrollStrip advances (and auto-follows) as each real step lands.
 */
export const useDeploymentTimeline = (steps: readonly DeploymentStep[], deploying: boolean): DeploymentTimeline => {
  const timeline = React.useMemo(() => buildTimeline(steps), [steps]);
  const historyCount = React.useMemo(() => steps.filter((s) => HISTORY_STATES.has(s.state)).length, [steps]);
  const done = !deploying;
  // While deploying, the first non-history entry (the active / next-up step) is the
  // in-progress leading edge; never reach the terminal confirm card. Once done, jump to
  // the terminal so the confirmation card is appended as the final strip card.
  const cursor = done
    ? timeline.length
    : Math.min(Math.max(historyCount + 1, 1), Math.max(1, timeline.length - 1));

  return { timeline, cursor, done };
};

export default useDeploymentTimeline;
